import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";
import "./ProductTransactions.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
const PAGE_SIZE_KEY = "ogestyle_product_transactions_page_size";

const moneyFromCents = (value) => `$${(Number(value || 0) / 100).toFixed(2)}`;

const toDateInput = (date) => {
  const value = new Date(date || Date.now());
  if (Number.isNaN(value.getTime())) return "";
  return value.toISOString().slice(0, 10);
};

const daysAgo = (days) => {
  const value = new Date();
  value.setDate(value.getDate() - days);
  return toDateInput(value);
};

const ProductTransactions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => Number(localStorage.getItem(PAGE_SIZE_KEY) || 25));
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(() => daysAgo(30));
  const [toDate, setToDate] = useState(() => toDateInput(new Date()));
  const [paymentStatus, setPaymentStatus] = useState("completed");
  const [pickupStatus, setPickupStatus] = useState("all");
  const [missingFeeData, setMissingFeeData] = useState(false);
  const [expandedOrderKey, setExpandedOrderKey] = useState("");

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = JSON.parse(
        decodeURIComponent(
          atob(base64)
            .split("")
            .map((char) => `%${("00" + char.charCodeAt(0).toString(16)).slice(-2)}`)
            .join(""),
        ),
      );

      const roles = Array.isArray(decoded.roles) ? decoded.roles : [];
      const hasAccess = decoded.role === "portal_admin" || decoded.role === "R6" || roles.includes("portal_admin") || roles.includes("R6");
      if (!hasAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (_error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchTransactions = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search,
        fromDate,
        toDate,
        paymentStatus,
        pickupStatus,
        missingFeeData: String(missingFeeData),
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/product-transactions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTransactions(response.data?.transactions || []);
      setTotal(Number(response.data?.total || 0));
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Failed to load product transactions.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, limit, missingFeeData, page, paymentStatus, pickupStatus, search, toDate]);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchTransactions();
    }
  }, [checkPortalAdmin, fetchTransactions]);

  const updateLimit = (value) => {
    const nextLimit = Number(value);
    setLimit(nextLimit);
    setPage(1);
    localStorage.setItem(PAGE_SIZE_KEY, String(nextLimit));
  };

  const resetAndFetch = () => {
    setPage(1);
    fetchTransactions();
  };

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard">
        <div className="portal-header">
          <div>
            <h1>Product Transactions</h1>
            <p>Item-level product fee, payout, seller, and Stripe transaction snapshots.</p>
          </div>
        </div>

        <div className="dashboard-card product-transactions-filter-card">
          <div className="product-transactions-filter-grid">
            <label className="transaction-filter-field transaction-filter-search">
              <span>Search</span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Order, customer, product" />
            </label>
            <label className="transaction-filter-field">
              <span>From</span>
              <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
            </label>
            <label className="transaction-filter-field">
              <span>To</span>
              <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
            </label>
            <label className="transaction-filter-field">
              <span>Payment</span>
              <select value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value)}>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="all">All</option>
              </select>
            </label>
            <label className="transaction-filter-field">
              <span>Pickup</span>
              <select value={pickupStatus} onChange={(event) => setPickupStatus(event.target.value)}>
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="picked_up">Picked up</option>
              </select>
            </label>
            <label className="transaction-filter-field transaction-filter-small">
              <span>Page Size</span>
              <select value={limit} onChange={(event) => updateLimit(event.target.value)}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
          <div className="product-transactions-filter-actions">
            <label className="transaction-filter-checkbox">
              <input type="checkbox" checked={missingFeeData} onChange={(event) => setMissingFeeData(event.target.checked)} />
              <span>Show missing/zero fee rows only</span>
            </label>
            <button className="portal-btn primary" onClick={resetAndFetch} disabled={loading}>Apply</button>
          </div>
        </div>

        {error ? <div className="error-message">{error}</div> : null}

        <div className="dashboard-card product-transactions-card">
          {loading ? (
            <p>Loading product transactions...</p>
          ) : transactions.length === 0 ? (
            <p>No product transactions found.</p>
          ) : (
            <table className="product-transactions-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Subtotal</th>
                  <th>Platform Fee</th>
                  <th>Seller Net</th>
                  <th>Seller</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((row) => {
                  const rowKey = `${row.parentOrderId}-${row.itemId}`;
                  const isExpanded = expandedOrderKey === rowKey;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr className={isExpanded ? "is-expanded" : ""}>
                        <td>
                          <button
                            type="button"
                            className="transaction-order-link"
                            onClick={() => setExpandedOrderKey(isExpanded ? "" : rowKey)}
                            aria-expanded={isExpanded}
                          >
                            {row.orderNumber || "-"}
                          </button>
                        </td>
                        <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}</td>
                        <td>{row.productName || "Product"}</td>
                        <td>{Number(row.quantity || 0)}</td>
                        <td>{moneyFromCents(row.itemSubtotalCents)}</td>
                        <td>{moneyFromCents(row.platformFeeAmount)}</td>
                        <td>{moneyFromCents(row.sellerNetAmount)}</td>
                        <td>{row.sellerName || row.shopName || "-"}</td>
                      </tr>
                      {isExpanded ? (
                        <tr className="transaction-detail-row">
                          <td colSpan={8}>
                            <table className="transaction-detail-table">
                              <colgroup>
                                <col className="detail-label-col" />
                                <col className="detail-value-col" />
                                <col className="detail-label-col" />
                                <col className="detail-value-col" />
                              </colgroup>
                              <tbody>
                                <tr>
                                  <th>Shop</th>
                                  <td>{row.shopName || "-"}</td>
                                  <th>Customer</th>
                                  <td>{row.customerEmail || "-"}</td>
                                </tr>
                                <tr>
                                  <th>Payment Status</th>
                                  <td>{row.paymentStatus || "-"}</td>
                                  <th>Pickup Status</th>
                                  <td>{row.displayPickupStatus || row.pickupStatus || row.sellerOrderPickupStatus || "-"}</td>
                                </tr>
                                <tr>
                                  <th>Stripe Fee</th>
                                  <td>{moneyFromCents(row.stripeFeeAmount)}</td>
                                  <th>Stripe Reference</th>
                                  <td>{row.stripePaymentIntentId || row.stripeCheckoutSessionId || "-"}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", gap: "12px", flexWrap: "wrap" }}>
          <span>{total} transactions · page {page} of {pageCount}</span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="portal-btn secondary" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
            <button className="portal-btn secondary" disabled={page >= pageCount || loading} onClick={() => setPage((current) => Math.min(pageCount, current + 1))}>Next</button>
          </div>
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default ProductTransactions;